import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_RESET_MS = 2000;

/**
 * Copies text to the clipboard and reports success for a short while, so a button
 * can show a "Copied!" state without every call site re-implementing the state, the
 * reset timer and its cleanup.
 *
 * Call it once per button: two buttons sharing one hook would flash success on both.
 */
export const useCopyToClipboard = (resetMs = DEFAULT_RESET_MS) => {
  const [isCopied, setIsCopied] = useState(false);

  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    },
    []
  );

  // Never rejects: a clipboard the browser refuses (permissions, insecure origin)
  // must not surface as an unhandled rejection, and isCopied simply stays false.
  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);

        setIsCopied(true);

        if (copyTimerRef.current) {
          clearTimeout(copyTimerRef.current);
        }

        copyTimerRef.current = setTimeout(() => setIsCopied(false), resetMs);
      } catch (error) {
        console.error('Failed to copy to the clipboard', error);
      }
    },
    [resetMs]
  );

  return { isCopied, copy };
};
